/**
 * The buyer's private notes on one lot (BXS-3627, M19).
 *
 * Saeed, 18 August: *"أنا بشوف الـ catalog، طب عجبني الحصان ده، ما هو في
 * ملاحظات، بحطه في الـ list، ما في."* This is the "ملاحظات" half; the star is
 * the "list" half.
 *
 * WHY NOT REUSE `horse-notes.component`
 * -------------------------------------
 * The native one (1,600 lines across three files) is a generic field engine:
 * every field is a config object, every field saves independently, every field
 * renders through the same six branches. It also posts to `saveHorseNote` with
 * an `auction_item_id`, which a Tattersalls lot does not have and never will.
 *
 * So this is written directly against the four fields that exist, using the
 * SAME vocabularies — the rating grades, the conformation terms and the scope
 * values are the platform's own enums, shared verbatim. A buyer moving between
 * the two sees the same words and means the same things by them; only the
 * machinery differs.
 *
 * WHY IT SAVES ALL FOUR FIELDS AT ONCE
 * ------------------------------------
 * The native panel saves per field, which is the right choice when each field
 * is a dropdown that closes on selection. Here the note is one thought — a
 * grade, some ticks and a sentence about the near fore — and a buyer who types
 * a sentence and picks a grade expects one Save to keep both. Per-field saving
 * would mean the sentence silently persisting on blur while the grade waited
 * for a second interaction.
 */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';

import { LocaleLinkPipe } from 'src/app/Shared/_pipes/locale-link/locale-link.pipe';
import { InternationalMyListService } from '../../../../_services/international-my-list.service';
import {
  LOT_CONFORMATIONS,
  LOT_RATINGS,
  LOT_SCOPES,
  LotNote,
} from '../../_mock/international-auctions.models';

/** How long a note may be. Mirrors the server's `max:1000`. */
const MAX_NOTE_LENGTH = 1000;

@Component({
  selector: 'app-international-lot-notes',
  standalone: true,
  imports: [FormsModule, RouterLink, TranslatePipe, LocaleLinkPipe],
  templateUrl: './international-lot-notes.component.html',
  styleUrl: './international-lot-notes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternationalLotNotesComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input({ required: true }) lotId!: number;

  /** Raised when the note is saved or cleared, so the page can re-badge. */
  @Output() saved = new EventEmitter<LotNote | null>();

  readonly ratings = LOT_RATINGS;
  readonly conformations = LOT_CONFORMATIONS;
  readonly scopes = LOT_SCOPES;
  readonly maxLength = MAX_NOTE_LENGTH;

  /** The working copy. Committed to the server only on Save. */
  rating: string | null = null;
  text = '';
  pickedConformations = new Set<string>();
  pickedScopes = new Set<string>();

  loading = false;
  saving = false;
  /** Shown for a beat after a successful save, then cleared. */
  justSaved = false;
  /** True once the buyer changes anything, which is what enables Save. */
  dirty = false;
  /** True when the server holds a note for this lot, which is what enables Delete. */
  hasStoredNote = false;
  failed = false;

  readonly signInLink = ['/buyer', 'auth', 'login'];

  private savedTimer: ReturnType<typeof setTimeout> | null = null;
  private sub?: Subscription;

  constructor(
    private readonly myList: InternationalMyListService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  get signedIn(): boolean {
    return this.myList.isEnabled;
  }

  get remaining(): number {
    return this.maxLength - this.text.length;
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The lot profile keeps this component alive across prev/next — the route
    // param changes without the component being destroyed. Without this, lot
    // 148 would show lot 147's note.
    if (changes['lotId'] && !changes['lotId'].firstChange) {
      this.reset();
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    if (this.savedTimer) {
      clearTimeout(this.savedTimer);
    }
  }

  /* ---------------------------------------------------------------- *
   * Editing
   * ---------------------------------------------------------------- */

  /** A second press on the chosen grade clears it — there is no "no grade" chip. */
  pickRating(value: string): void {
    this.rating = this.rating === value ? null : value;
    this.touch();
  }

  toggleConformation(value: string): void {
    this.flip(this.pickedConformations, value);
    this.touch();
  }

  toggleScope(value: string): void {
    this.flip(this.pickedScopes, value);
    this.touch();
  }

  onTextInput(event: Event): void {
    this.text = (event.target as HTMLTextAreaElement).value.slice(0, this.maxLength);
    this.touch();
  }

  isConformationOn(value: string): boolean {
    return this.pickedConformations.has(value);
  }

  isScopeOn(value: string): boolean {
    return this.pickedScopes.has(value);
  }

  /* ---------------------------------------------------------------- *
   * Persistence
   * ---------------------------------------------------------------- */

  save(): void {
    if (!this.signedIn || this.saving) {
      return;
    }

    this.saving = true;
    this.failed = false;
    this.cdr.markForCheck();

    this.sub?.unsubscribe();
    this.sub = this.myList
      .saveNote(this.lotId, {
        rating: this.rating,
        notes: this.text.trim(),
        confirmation_type: [...this.pickedConformations],
        scope_type: [...this.pickedScopes],
      })
      .subscribe({
        next: (result) => {
          this.saving = false;

          if (!result.ok) {
            this.failed = true;
            this.cdr.markForCheck();
            return;
          }

          this.dirty = false;
          this.hasStoredNote = true;
          this.flash();
          this.saved.emit(result.note ?? null);
          this.cdr.markForCheck();
        },
        error: () => {
          this.saving = false;
          this.failed = true;
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Clear the note. The lot stays starred — un-noting is not un-listing, and
   * the star has its own control right beside this one.
   */
  clear(): void {
    if (!this.signedIn || this.saving || !this.hasStoredNote) {
      return;
    }

    this.saving = true;
    this.failed = false;
    this.cdr.markForCheck();

    this.sub?.unsubscribe();
    this.sub = this.myList.deleteNote(this.lotId).subscribe({
      next: (result) => {
        this.saving = false;

        if (!result.ok) {
          this.failed = true;
          this.cdr.markForCheck();
          return;
        }

        this.reset();
        this.saved.emit(null);
        this.cdr.markForCheck();
      },
      error: () => {
        this.saving = false;
        this.failed = true;
        this.cdr.markForCheck();
      },
    });
  }

  /* ---------------------------------------------------------------- *
   * Internals
   * ---------------------------------------------------------------- */

  private load(): void {
    if (!this.signedIn) {
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();

    this.sub?.unsubscribe();
    this.sub = this.myList.getNote(this.lotId).subscribe({
      next: (note) => {
        this.loading = false;
        this.apply(note);
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  private apply(note: LotNote | null): void {
    this.hasStoredNote = note !== null;
    this.rating = note?.rating ?? null;
    this.text = note?.notes ?? '';
    this.pickedConformations = new Set(note?.confirmation_type ?? []);
    this.pickedScopes = new Set(note?.scope_type ?? []);
    this.dirty = false;
  }

  private reset(): void {
    this.apply(null);
    this.failed = false;
    this.justSaved = false;
  }

  private touch(): void {
    this.dirty = true;
    this.justSaved = false;
    this.cdr.markForCheck();
  }

  private flip(set: Set<string>, value: string): void {
    if (set.has(value)) {
      set.delete(value);
    } else {
      set.add(value);
    }
  }

  private flash(): void {
    this.justSaved = true;
    if (this.savedTimer) {
      clearTimeout(this.savedTimer);
    }
    this.savedTimer = setTimeout(() => {
      this.justSaved = false;
      this.cdr.markForCheck();
    }, 2400);
  }
}
